import React from 'react';
import { Dropdown, Button, Typography, Space } from 'antd';
import { 
  MenuOutlined, 
  ArrowLeftOutlined, 
  DatabaseOutlined, 
  WifiOutlined,
  HomeOutlined,
  UserOutlined,
  SettingOutlined,
  ToolOutlined,
  RobotOutlined,
  MessageOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  BankOutlined,
  ApartmentOutlined,
  UnorderedListOutlined,
  CreditCardOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  CalendarOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useModelSelector } from '../../contexts/ModelSelectorContext';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { isPlatformAdministrator } from '../AppShell/appNavAccess';

const { Text } = Typography;

interface HamburgerMenuProps {
  onNavigateBack?: () => void;
  onDataSourceChange?: (source: 'live' | 'sample') => void;
}

export const HamburgerMenu: React.FC<HamburgerMenuProps> = ({
  onNavigateBack,
  onDataSourceChange
}) => {
  const { dataSource, setDataSource } = useModelSelector();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUser();
  const isPlatformAdmin = isPlatformAdministrator(user);

  const handleDataSourceChange = (source: 'live' | 'sample') => {
    setDataSource(source);
    onDataSourceChange?.(source);
  };

  const menuItems = [
    {
      key: 'navigation',
      label: <Text strong>Navigation</Text>,
      type: 'group' as const,
    },
    {
      key: 'back-to-main',
      icon: <ArrowLeftOutlined />,
      label: 'Back to Main UI',
      onClick: onNavigateBack,
    },
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: 'Control Bridge',
      onClick: () => navigate('/dashboard'),
    },
    ...(isAuthenticated ? [
      {
        type: 'divider' as const,
      },
      {
        key: 'chat-operational',
        icon: <MessageOutlined />,
        label: 'Exo Panel',
        onClick: () => navigate('/chat-operational'),
      },
      {
        type: 'divider' as const,
      },
      {
        key: 'pages',
        label: <Text strong>Pages</Text>,
        type: 'group' as const,
      },
      {
        key: 'upcoming-sessions',
        icon: <CalendarOutlined />,
        label: 'My Sessions',
        onClick: () => navigate('/upcoming-sessions'),
      },
      ...(isLocalBridgeApiEnabled() ? [{
        key: 'local-bridge',
        icon: <LinkOutlined />,
        label: 'Local Bridge',
        onClick: () => navigate('/local-bridge'),
      }] : []),
      {
        key: 'prime-artifacts',
        icon: <SettingOutlined />,
        label: 'Prime Artifacts',
        onClick: () => navigate('/prime/artifacts'),
      },
      {
        key: 'processes',
        icon: <ToolOutlined />,
        label: 'Process Relationships',
        onClick: () => navigate('/processes'),
      },
      {
        key: 'configuration-submenu',
        label: 'Configuration',
        children: [
          {
            key: 'artifact-types',
            icon: <DatabaseOutlined />,
            label: 'Artifact Types',
            onClick: () => navigate('/artifact-types'),
          },
          {
            key: 'state-templates',
            icon: <SettingOutlined />,
            label: 'State Templates',
            onClick: () => navigate('/state-templates'),
          },
          {
            key: 'option-catalogs',
            icon: <UnorderedListOutlined />,
            label: 'Option Catalogs',
            onClick: () => navigate('/option-catalogs'),
          },
          {
            key: 'skills',
            icon: <TeamOutlined />,
            label: 'Skills Taxonomy',
            onClick: () => navigate('/skills'),
          },
        ],
      },
      {
        key: 'demo',
        icon: <PlayCircleOutlined />,
        label: 'Interactive Demo',
        onClick: () => navigate('/demo'),
      },
      // Platform admin only — closes open subscription-management chrome for all auth users
      ...(isPlatformAdmin
        ? [
            {
              type: 'divider' as const,
            },
            {
              key: 'krysalis-admin',
              label: <Text strong>Krysalis Admin</Text>,
              type: 'group' as const,
            },
            {
              key: 'admin-submenu',
              label: 'Administration',
              children: [
                {
                  key: 'user-management',
                  icon: <UserOutlined />,
                  label: 'User Management',
                  onClick: () => navigate('/user-management'),
                },
                {
                  key: 'company-management',
                  icon: <BankOutlined />,
                  label: 'Company Management',
                  onClick: () => navigate('/company-management'),
                },
                {
                  key: 'subscription-management',
                  icon: <CreditCardOutlined />,
                  label: 'Subscription Management',
                  onClick: () => navigate('/subscription-management'),
                },
                {
                  key: 'subscription-types-management',
                  icon: <AppstoreOutlined />,
                  label: 'Subscription Types & Features',
                  onClick: () => navigate('/subscription-types-management'),
                },
                {
                  key: 'company-instances',
                  icon: <ApartmentOutlined />,
                  label: 'User-company links',
                  onClick: () => navigate('/company-instances'),
                },
                ...(isLocalBridgeApiEnabled()
                  ? [
                      {
                        key: 'bridge-migration',
                        icon: <LinkOutlined />,
                        label: 'Bridge Migration',
                        onClick: () => navigate('/admin/bridge-migration'),
                      },
                    ]
                  : []),
                {
                  key: 'admin-bridge-session-schedule',
                  icon: <CalendarOutlined />,
                  label: 'Session Schedule',
                  onClick: () => navigate('/admin/bridge-session-schedule'),
                },
                {
                  key: 'artifact-tree-manager',
                  icon: <DatabaseOutlined />,
                  label: 'Artifact Tree Manager',
                  onClick: () => navigate('/admin/artifact-tree-manager'),
                },
                {
                  key: 'global-properties',
                  icon: <GlobalOutlined />,
                  label: 'Global Properties',
                  onClick: () => navigate('/admin/global-properties'),
                },
                {
                  key: 'krybot-dashboard',
                  icon: <RobotOutlined />,
                  label: 'Krybot Dashboard',
                  onClick: () => navigate('/krybot'),
                },
                {
                  key: 'gpt-integration',
                  icon: <RobotOutlined />,
                  label: 'GPT Assistant',
                  onClick: () => navigate('/gpt-integration'),
                },
                {
                  key: 'architecture-agent',
                  icon: <ToolOutlined />,
                  label: 'Architecture Agent',
                  onClick: () => navigate('/architecture-agent'),
                },
                ...(process.env.NODE_ENV === 'development'
                  ? [
                      {
                        key: 'development',
                        icon: <ToolOutlined />,
                        label: 'Development',
                        onClick: () => navigate('/development'),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      // Data source selector - only in development
      ...(process.env.NODE_ENV === 'development' ? [
        {
          type: 'divider' as const,
        },
        {
          key: 'data-source',
          label: <Text strong>Data Source</Text>,
          type: 'group' as const,
        },
        {
          key: 'live-data',
          icon: <WifiOutlined />,
          label: (
            <Space>
              <span>Live Data</span>
              {dataSource === 'live' && <Text type="success">(Active)</Text>}
            </Space>
          ),
          onClick: () => handleDataSourceChange('live'),
          disabled: dataSource === 'live',
        },
        {
          key: 'sample-data',
          icon: <DatabaseOutlined />,
          label: (
            <Space>
              <span>Sample Data</span>
              {dataSource === 'sample' && <Text type="success">(Active)</Text>}
            </Space>
          ),
          onClick: () => handleDataSourceChange('sample'),
          disabled: dataSource === 'sample',
        },
      ] : []),
    ] : []),
  ];

  const menu = {
    items: menuItems,
    style: { minWidth: '200px' }
  };

  return (
    <Dropdown menu={menu} trigger={['click']} placement="bottomLeft">
      <Button
        type="text"
        icon={<MenuOutlined />}
        style={{ height: '40px', borderRadius: '8px' }}
      />
    </Dropdown>
  );
};
